import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getActiveAccount } from "@containers/Account/store/selectors";
import { InputPicker, Table, PaginationFacade, usePagination, DatPeriodPicker } from "@shared/components";
import { NameOfChildRoutes } from "@shared/constants";
import { getCountiesByState, getStates } from "@shared/store/actions";
import { actions as PayPerLeadActions, selectors as PayPerLeadSelectors } from "@containers/PayPerLead/store";
import { NoFilteredLead } from "@containers/PayPerLead/components/PayPerLeadDashboardContainer/EmptyState";
import { getLeadsTableProperties } from "@containers/PayPerLead/components/PayPerLeadDashboardContainer/LeadsList/leadsTableProperties";
import { useNavigate } from "react-router-dom";
import { UnpaidSubscriptions } from "@containers/PayPerLead/components/PayPerLeadDashboardContainer";
import moment from "moment";
import { ORDER_COLUMN_TYPE, ORDER_TYPE, PayPerLeadFilter } from "@shared/interfaces";
import { isPaymentRequired } from "@shared/utils/subscriptions";
import { PayPerLead } from "@shared/models/PayPerLead";
import { useLocation } from "react-router";
import { ReactComponent as DatePickerChevronDown } from "@assets/files/icons/general/datepicker-chevron-down.svg";
import Card from "@shared/components/Card/Card";
import { checkIfAdmin } from "@shared/utils/ACL";
import { useFeatureEnabledCheck } from "@shared/hooks/FeatureHook";
import { Feature } from "@shared/models";
import { useLazyGetReiSiftIntegrationSummaryQuery } from "@shared/api/reiSiftIntegrationApi";

import { MonthlyBalance, MonthlyLeads, PPLBuyer } from "./components";

import "./index.scss";

const LeadsList: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const account = useSelector(getActiveAccount());
  const leads = useSelector(PayPerLeadSelectors.getLeads());
  const subscriptions = useSelector(PayPerLeadSelectors.getSubscriptions());
  const filter = useSelector(PayPerLeadSelectors.getFilter());
  const states = useSelector(PayPerLeadSelectors.availableStates);
  const counties = useSelector(PayPerLeadSelectors.availableCounties);
  const { pagination, onPageChangeAndScroll } = usePagination();

  const setFilter = (data: Partial<PayPerLeadFilter>) => {
    onPageChangeAndScroll({ ...pagination, currentPage: 1 });
    dispatch(PayPerLeadActions.updateFilter(data));
  };

  const isReiSiftFeatureEnabled = useFeatureEnabledCheck(Feature.ReiSift);

  const [triggerGetReiSiftSummary, { data: reiSiftIntegrationSummary }] = useLazyGetReiSiftIntegrationSummaryQuery();

  const properties = useMemo(
    () => getLeadsTableProperties(!!reiSiftIntegrationSummary?.status),
    [reiSiftIntegrationSummary],
  );

  useEffect(() => {
    const isAdmin = checkIfAdmin();

    if (isReiSiftFeatureEnabled && account && !isAdmin) {
      triggerGetReiSiftSummary({ account_id: account.id });
    }
  }, [triggerGetReiSiftSummary, account, isReiSiftFeatureEnabled]);

  useEffect(() => {
    if (states) return;

    dispatch(getStates.request());
  }, [dispatch, states]);

  useEffect(() => {
    dispatch(PayPerLeadActions.fetchFilterOptions.request({ is_inactive: false, is_full_range: true }));

    return () => {
      dispatch(PayPerLeadActions.resetFilterOptions());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!filter.stateId) return;

    dispatch(getCountiesByState.request(+filter.stateId));
  }, [filter.stateId, dispatch]);

  useEffect(() => {
    if (!account?.id) return;

    dispatch(
      PayPerLeadActions.getLeads.request({
        is_active: true,
        account_id: account.id,
        page: pagination.currentPage,
        limit: pagination.resultsPerPage,
        state_id: filter.stateId,
        county_id: filter.countyId,
        order: ORDER_COLUMN_TYPE.CREATED_AT,
        direction: ORDER_TYPE.DESC,
        show_detail: true,
        show_county: true,
        show_state: true,
        ...(filter.from && { from: moment(filter.from).utc(true).toDate() }),
        ...(filter.to && { to: moment(filter.to).utc(true).toDate() }),
      }),
    );
  }, [
    account?.id,
    pagination.currentPage,
    pagination.resultsPerPage,
    filter.from,
    filter.to,
    filter.stateId,
    filter.countyId,
    dispatch,
  ]);

  const unpaidSubscriptions = (subscriptions ?? []).filter((s) => {
    const item = s.items.find((item) => {
      if (item.state_id !== Number(filter.stateId)) {
        return false;
      }

      if (item.all_counties) {
        return true;
      }

      const countyIdNumeric = Number(filter.countyId);

      return !!item.counties.find((countyItem) => countyItem.county_id === countyIdNumeric);
    });

    return item && isPaymentRequired(s);
  });

  const goToLead = (lead: PayPerLead) =>
    navigate(`${NameOfChildRoutes.PAY_PER_LEAD.ROOT}/${lead.id}`, {
      state: { from: location.pathname + location.search },
    });

  return (
    <div className="pay-per-lead-list">
      <div className="pay-per-lead-list-card-wrapper">
        <Card>
          <MonthlyBalance subscriptions={subscriptions ?? []} />
        </Card>
        <Card>
          <MonthlyLeads subscriptions={subscriptions ?? []} />
        </Card>
        <Card>
          <PPLBuyer />
        </Card>
      </div>

      {unpaidSubscriptions && unpaidSubscriptions.length > 0 ? (
        <UnpaidSubscriptions unpaidSubscriptions={unpaidSubscriptions} />
      ) : null}

      {!!leads && (
        <>
          <div className="pay-per-lead-list-title ">Your Leads</div>
          <div className="pay-per-lead-list-filter-wrapper">
            <div className="pay-per-lead-list-filter">
              <InputPicker
                name="States"
                placeholder="All States"
                searchable
                className="select-picker select-picker-state"
                disableContainerStyles
                cleanable
                value={filter.stateId?.toString() || ""}
                data={
                  states?.map(({ id, name }) => ({
                    value: String(id),
                    label: name,
                  })) ?? []
                }
                onChange={(stateId) => setFilter({ stateId, countyId: undefined })}
                onClean={() => setFilter({ stateId: undefined, countyId: undefined })}
              />
              <InputPicker
                disabled={!filter.stateId}
                name="County"
                placeholder="All Counties"
                className="select-picker select-picker-state"
                searchable
                disableContainerStyles
                cleanable
                value={filter.countyId?.toString() || ""}
                data={
                  counties?.map(({ id, name }) => ({
                    value: String(id),
                    label: name,
                  })) ?? []
                }
                onChange={(countyId) => setFilter({ countyId })}
                onClean={() => setFilter({ countyId: undefined })}
              />

              <DatPeriodPicker
                placeholder="All Dates"
                className="date-period-picker"
                caretAs={DatePickerChevronDown}
                onClean={() => setFilter({ from: "", to: "" })}
                value={filter.from && filter.to ? [moment(filter.from).toDate(), moment(filter.to).toDate()] : null}
                onChange={(dateRange) => {
                  if (dateRange) {
                    const [from, to] = dateRange;

                    setFilter({
                      from: moment(from).startOf("day").toISOString(),
                      to: moment(to).endOf("day").toISOString(),
                    });
                  }
                }}
              />
            </div>
          </div>

          {leads.items.length ? (
            <>
              <Table items={leads?.items || []} properties={properties} onRowClick={goToLead} />

              <PaginationFacade
                currentPage={pagination.currentPage}
                resultsPerPage={pagination.resultsPerPage}
                totalData={leads?.total}
                onPaginationChange={onPageChangeAndScroll}
              />
            </>
          ) : (
            <NoFilteredLead />
          )}
        </>
      )}
    </div>
  );
};

export default LeadsList;
